import { FC, useState } from 'react'
import {
  BooleanField, Button, Datagrid, DateField, DeleteWithConfirmButton, EditButton,
  FunctionField, NumberField, Show, TabbedShowLayout,
  TextField, TopToolbar, useRecordContext, useRedirect, useGetList, useList,
  ListContextProvider, Pagination, useTranslate, Tab,
} from 'react-admin'
import AddIcon from '@mui/icons-material/Add'
import LinkIcon from '@mui/icons-material/Link'
import CodeIcon from '@mui/icons-material/Code'
import DescriptionIcon from '@mui/icons-material/Description'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button as MuiButton, Typography } from '@mui/material'
import {
  DetailCard, InfoRow, StatCard, StatusChip, SuccessChip, EnabledChip, MethodChip,
  CopyBlock, LogsChart, Step, ProxyLinksDatagrid, SearchField, SyncButton,
  ToolEnabledSwitch, ToolSourceChip, ToggleAllTools, formatDateTime,
} from '../../components'
import { xxllncColor } from '../../layout/theme/color'
import { fadeSlideIn } from '../../components/dashboard'
import {
  OrganizationProxyLinkOut, ProxyOut, ProxyToolOut, ProxyUsageLogOut,
} from '../../types/apiRequests'
import { matchesSearch } from '../../utils/matchesSearch'

const DocsButton: FC = () => {
  const record = useRecordContext<ProxyOut>()
  if (!record?.name) return null
  const docsUrl = `${window.location.origin}/api/v1/p/${record.name}/docs`
  return (
    <Button label="API Docs" onClick={() => window.open(docsUrl, '_blank', 'noopener,noreferrer')}>
      <DescriptionIcon />
    </Button>
  )
}

const ShowActions: FC = () => {
  const translate = useTranslate()
  return (
    <TopToolbar>
      <DocsButton />
      <EditButton />
      <DeleteWithConfirmButton
        confirmTitle={translate('delete.confirmTitle')}
        confirmContent={translate('delete.confirmContentProxy')}
      />
    </TopToolbar>
  )
}

const SetupTab: FC = () => {
  const record = useRecordContext<ProxyOut>()
  const translate = useTranslate()

  if (!record) return null

  const baseUrl = window.location.origin
  const proxyUrl = `${baseUrl}/api/v1/p/${record.name}/`

  const curlExample = `${translate('proxySetup.curlComment')}
curl "${proxyUrl}{pad-naar-api}" \\
  --cert client.crt \\
  --key client.key`

  return (
    <Box sx={{ maxWidth: 800, py: 1 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{translate('proxySetup.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{translate('proxySetup.intro')}</Typography>
      </Box>

      <Step
        number={1}
        icon={<LinkIcon fontSize="small" />}
        iconBg={xxllncColor.opGreen10}
        iconColor={xxllncColor.green100}
        title={translate('proxySetup.step1Title')}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('proxySetup.step1Description')}</Typography>
        <CopyBlock content={proxyUrl} />
      </Step>

      <Step
        number={2}
        icon={<CodeIcon fontSize="small" />}
        iconBg={xxllncColor.opOrange20}
        iconColor={xxllncColor.orange100}
        title={translate('proxySetup.step2Title')}
        last
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('proxySetup.step2Description')}</Typography>
        <CopyBlock content={curlExample} />
      </Step>
    </Box>
  )
}

const ToolsTab: FC = () => {
  const record = useRecordContext<ProxyOut>()
  const redirect = useRedirect()
  const translate = useTranslate()
  const [search, setSearch] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)

  const isManual = record?.tool_source === 'manual'
  const isOpenapi = record?.tool_source === 'openapi'

  const { data, isLoading } = useGetList<ProxyToolOut>(
    'proxy_tool',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { proxy_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const disabledCount = (data ?? []).filter((t) => !t.enabled).length

  const filtered = (data ?? []).filter((row) => {
    if (!showDisabled && !row.enabled) return false
    if (!search) return true
    return matchesSearch([row.name, row.description, row.path, row.http_method], search.toLowerCase())
  })

  const listContext = useList({ data: filtered, isLoading, perPage: 25 })

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {isManual && (
          <Button
            label="resources.proxy_tool.create"
            onClick={() => redirect('create', 'proxy_tool', undefined, undefined, { proxyId: record?.id })}
          >
            <AddIcon />
          </Button>
        )}
        {isOpenapi && <SyncButton resource="proxy" />}
        {data && data.length > 1 && (
          <ToggleAllTools parentResource="proxy" parentId={String(record?.id ?? '')} tools={data ?? []} />
        )}
        {disabledCount > 0 && (
          <MuiButton
            size="small"
            variant={showDisabled ? 'contained' : 'outlined'}
            startIcon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowDisabled(!showDisabled)}
            sx={{
              'textTransform': 'none', 'fontWeight': 600, 'fontSize': 12,
              'borderColor': 'rgba(0,0,0,0.15)', 'color': showDisabled ? xxllncColor.base : 'rgba(0,0,0,0.55)',
              'bgcolor': showDisabled ? 'rgba(0,0,0,0.35)' : 'transparent',
              '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: showDisabled ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            {translate('data.hideDisabled', { count: disabledCount })}
          </MuiButton>
        )}
        <SearchField value={search} onChange={setSearch} />
      </Box>
      {isOpenapi && record?.openapi_url && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('data.openapiSource')}: {record.openapi_url}
        </Alert>
      )}
      <ListContextProvider value={listContext}>
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <TextField source="name" />
          <TextField source="description" />
          <FunctionField label="data.httpMethod" render={() => <MethodChip />} />
          <TextField source="path" />
          <FunctionField label="data.enabled" render={() => <ToolEnabledSwitch resource="proxy_tool" />} sortable={false} />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </>
  )
}

const LogsTab: FC = () => {
  const record = useRecordContext<ProxyOut>()
  const translate = useTranslate()
  const [search, setSearch] = useState('')

  const { data, isLoading } = useGetList<ProxyUsageLogOut>(
    'proxy_usage_log',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { proxy_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const filtered = (data ?? []).filter((row) => {
    if (!search) return true
    return matchesSearch([row.organization_name, row.username, row.http_method, row.path, row.error_message], search.toLowerCase())
  })

  const listContext = useList({ data: filtered, isLoading, perPage: 25 })

  return (
    <>
      <LogsChart data={data} isLoading={isLoading} />
      <SearchField value={search} onChange={setSearch} placeholder={translate('data.searchLogs')} sx={{ mb: 2, display: 'block' }} />
      <ListContextProvider value={listContext}>
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <DateField source="created" showTime label="data.createdAt" />
          <TextField source="organization_name" label="data.organization" />
          <TextField source="username" label="data.username" />
          <FunctionField label="data.httpMethod" render={() => <MethodChip />} />
          <TextField source="path" label="data.path" />
          <NumberField source="response_time_ms" label="ms" />
          <FunctionField label="data.success" render={() => <SuccessChip />} />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </>
  )
}

const LinksTab: FC = () => {
  const record = useRecordContext<ProxyOut>()
  if (!record?.id) return null

  return (
    <ProxyLinksDatagrid
      // eslint-disable-next-line camelcase
      filter={{ proxy_id: record.id }}
      // eslint-disable-next-line camelcase
      createState={{ proxy_id: record.id }}
      showOrganization
    />
  )
}

const ProxyDetailsTab: FC = () => {
  const record = useRecordContext<ProxyOut>()
  const translate = useTranslate()

  const { total: toolCount } = useGetList<ProxyToolOut>(
    'proxy_tool',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { proxy_id: record?.id } },
    { enabled: !!record?.id },
  )
  const { total: linkCount } = useGetList<OrganizationProxyLinkOut>(
    'organization_proxy_link',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { proxy_id: record?.id } },
    { enabled: !!record?.id },
  )

  if (!record) return null

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: xxllncColor.codGray }}>
            {record.display_name}
          </Typography>
          <StatusChip active={record.enabled} />
          <ToolSourceChip source={record.tool_source} />
        </Box>
        {record.description && (
          <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, maxWidth: 600 }}>
            {record.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
        <StatCard value={toolCount ?? 0} label={translate('data.statTools')} accent={xxllncColor.dark100} delay={80} />
        <StatCard value={linkCount ?? 0} label={translate('data.statLinks')} accent={xxllncColor.orange100} delay={120} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <DetailCard title={translate('data.details')} delay={160}>
          <InfoRow label={translate('data.name')} value={record.name} mono />
          <InfoRow label={translate('data.application')} value={record.application_display_name} />
          <InfoRow label={translate('data.toolSource')} value={record.tool_source} />
        </DetailCard>

        <DetailCard title={translate('data.other')} delay={200}>
          {record.openapi_url && (
            <InfoRow label={translate('data.openapiUrl')} value={record.openapi_url} mono />
          )}
          <InfoRow label={translate('data.createdAt')} value={formatDateTime(record.created)} />
          <InfoRow label={translate('data.lastModified')} value={formatDateTime(record.lastModified)} />
        </DetailCard>
      </Box>
    </Box>
  )
}

const ProxyShow: FC = () => {
  const translate = useTranslate()

  return (
    <Show actions={<ShowActions />}>
      <TabbedShowLayout>
        <Tab label="resources.proxy.tabs.setup">
          <SetupTab />
        </Tab>
        <Tab label="resources.proxy.tabs.details" path="details">
          <ProxyDetailsTab />
        </Tab>
        <Tab label="resources.proxy.tabs.tools" path="tools">
          <ToolsTab />
        </Tab>
        <Tab label="resources.proxy.tabs.links" path="links">
          <LinksTab />
        </Tab>
        <Tab label="resources.proxy.tabs.logs" path="logs">
          <LogsTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default ProxyShow
